/**
 * Data Manager
 * Handles data persistence, state management, and API interactions
 */

import { logger } from '../utils/logger'
import { ApiService } from '../services/api'
import { AppState } from '../stores/app-state'

export class DataManager {
  private apiService: ApiService
  private appState: AppState

  constructor(apiService: ApiService, appState: AppState) {
    this.apiService = apiService
    this.appState = appState
    logger.debug('DataManager initialized')
  }

  /**
   * Save description list to database
   */
  public async saveDescriptionListToDatabase(listText: string): Promise<void> {
    const currentProject = this.appState.getCurrentProject()
    if (!currentProject) {
      throw new Error('No current project selected')
    }

    try {
      logger.debug('Saving description list to database for project:', currentProject.id)
      
      await this.apiService.updateProjectWorkflowData(currentProject.id, {
        numbered_list_text: listText
      })
      
      logger.debug('Description list saved successfully')
      
      // Update app state with the saved data
      this.appState.setDescriptionList(listText)
    } catch (error) {
      logger.error('Failed to save description list:', error)
      throw error
    }
  }

  /**
   * Load selections for current project
   */
  public async loadSelectionsForCurrentProject(): Promise<void> {
    const project = this.appState.getCurrentProject()
    if (!project) {
      logger.warn('No current project selected for loading selections')
      return
    }

    try {
      logger.debug('Loading selections for project:', project.id)
      
      const response = await this.apiService.listProjectSelections(project.id)
      const selections = response.selections
      
      if (selections && selections.length > 0) {
        this.appState.setSelections(selections)
        logger.debug(`Loaded ${selections.length} selections for project ${project.id}`)
      } else {
        logger.debug('No selections found for project:', project.id)
        this.appState.setSelections([])
      }
    } catch (error) {
      logger.error('Failed to load selections:', error)
      this.appState.setSelections([])
      throw error
    }
  }

  /**
   * Load project data in parallel
   */
  public async loadProjectDataParallel(project: any): Promise<void> {
    const loadingSteps = [
      { name: 'documents', fn: () => this.loadProjectDocuments(project.id) },
      { name: 'selections', fn: () => this.apiService.listProjectSelections(project.id) },
      { name: 'workflowData', fn: () => this.apiService.getProjectWorkflowData(project.id) }
    ]

    logger.debug('Loading project data in parallel for project:', project.id)

    try {
      const results = await Promise.allSettled(loadingSteps.map(step => step.fn()))
      
      const data: any = {}
      results.forEach((result, index) => {
        const step = loadingSteps[index]
        if (result.status === 'fulfilled') {
          data[step.name] = result.value
          logger.debug(`✅ Loaded ${step.name} for project ${project.id}`)
        } else {
          logger.error(`❌ Failed to load ${step.name} for project ${project.id}:`, result.reason)
          data[step.name] = step.name === 'selections' ? [] : null
        }
      })

      // Debug: Log what we received from the API
      logger.debug('Raw API response data:', data)
      logger.debug('Documents response:', data.documents)
      logger.debug('Selections response:', data.selections)
      logger.debug('Workflow data response:', data.workflowData)

      // Update app state with loaded data
      // Handle both new document format and legacy PDF format
      const documents = data.documents?.documents || data.documents?.pdfs || []
      this.appState.setDocuments(documents)
      this.appState.setSelections(data.selections?.selections || [])
      const descriptionList = data.workflowData?.numbered_list_text || ''
      this.appState.setDescriptionList(descriptionList)
      if (descriptionList) {
        this.appState.setHasFormattedText(true)
      }

      logger.debug('Project data loading completed for project:', project.id)
    } catch (error) {
      logger.error('Failed to load project data:', error)
      throw error
    }
  }

  /**
   * Load workflow data for project
   */
  public async loadWorkflowDataForProject(projectId: string): Promise<void> {
    try {
      logger.debug('Loading workflow data for project:', projectId)
      
      const [selectionsResponse, workflowData] = await Promise.all([
        this.apiService.listProjectSelections(projectId),
        this.apiService.getProjectWorkflowData(projectId)
      ])

      // Update app state
      this.appState.setSelections(selectionsResponse.selections || [])
      const descriptionList = workflowData.numbered_list_text || ''
      this.appState.setDescriptionList(descriptionList)
      if (descriptionList) {
        this.appState.setHasFormattedText(true)
      }

      logger.debug('Workflow data loaded successfully for project:', projectId)
    } catch (error) {
      logger.error('Failed to load workflow data:', error)
      throw error
    }
  }

  /**
   * Handle file upload completion
   */
  public async onFileUploaded(fileId: string, filename: string): Promise<void> {
    try {
      logger.info('File uploaded successfully:', filename, 'ID:', fileId)
      
      // Refresh project document list
      const currentProject = this.appState.getCurrentProject()
      if (currentProject) {
        logger.info('Refreshing project documents after upload for project:', currentProject.id)
        await this.loadProjectDocuments(currentProject.id)
        logger.info('Project documents refreshed after upload')
        
        // CRITICAL FIX: Update project's document_count to enable "Next" button
        const updatedProject = await this.apiService.getProject(currentProject.id)
        this.appState.setCurrentProject(updatedProject)
        logger.info('Project document_count updated after upload:', updatedProject.document_count)
      }

      // Show success message
      logger.info(`File "${filename}" uploaded successfully`)
    } catch (error) {
      logger.error('Failed to handle file upload:', error)
      throw error
    }
  }

  /**
   * Handle app state errors
   */
  public async handleAppStateError(error: string): Promise<void> {
    logger.error('App state error:', error)
    
    // Clear potentially corrupted state
    this.appState.setSelections([])
    this.appState.setCoordinates([])
    this.appState.setDescriptionList('')
    
    // Show error message to user
    logger.error('Application state has been reset due to an error')
  }

  /**
   * Load project documents (including PDFs, images, and manual text files)
   */
  public async loadProjectDocuments(projectId: string): Promise<void> {
    try {
      logger.debug('Loading project documents for project:', projectId)
      
      // Use new multi-document API
      const response = await this.apiService.getProjectDocuments(projectId)
      logger.debug('API response for getProjectDocuments:', response)
      
      const documents = response.documents || []
      logger.debug('Extracted documents array:', documents)
      logger.debug('Documents length:', documents.length)
      
      this.appState.setDocuments(documents)
      logger.debug('Loaded', documents.length, 'documents for project', projectId)
    } catch (error) {
      logger.error('Failed to load project documents:', error)
      throw error
    }
  }

  /**
   * Load project selections
   */
  public async loadProjectSelections(projectId: string): Promise<void> {
    try {
      logger.debug('Loading project selections for project:', projectId)
      
      const response = await this.apiService.listProjectSelections(projectId)
      logger.debug('API response for listProjectSelections:', response)
      
      const selections = response.selections || []
      logger.debug('Extracted selections array:', selections)
      logger.debug('Selections length:', selections.length)
      
      this.appState.setSelections(selections)
      logger.debug('Loaded', selections.length, 'selections for project', projectId)
    } catch (error) {
      logger.error('Failed to load project selections:', error)
      throw error
    }
  }

  /**
   * Save manual entry as a .txt document
   */
  public async saveManualEntry(text: string, modal?: any): Promise<void> {
    try {
      logger.debug('Saving manual entry as .txt document:', text.substring(0, 50) + '...')
      
      const currentProject = this.appState.getCurrentProject()
      if (!currentProject) {
        throw new Error('No current project selected')
      }

      // Generate filename based on timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      const filename = `manual-entry-${timestamp}.txt`

      // Create manual text document
      await this.apiService.createManualTextDocument(
        currentProject.id,
        text,
        filename
      )
      
      // Refresh both documents and selections from server to ensure consistency
      await this.loadProjectDocuments(currentProject.id)
      await this.loadProjectSelections(currentProject.id)
      
      // Trigger UI refresh to show new manual entry
      logger.debug('🔄 Triggering UI refresh after manual entry creation...')
      window.dispatchEvent(new CustomEvent('selections-updated', { 
        detail: { source: 'manual-entry-creation' } 
      }))
      
      logger.debug('Manual entry saved as .txt document successfully')
      
      // Close modal if provided
      if (modal) {
        modal.close()
      }
    } catch (error) {
      logger.error('Failed to save manual entry:', error)
      throw error
    }
  }

  /**
   * Save manual text edit for a document
   */
  public async saveManualTextEdit(documentId: string, text: string, modal?: any): Promise<void> {
    try {
      logger.debug('Saving manual text edit for document:', documentId)
      
      await this.apiService.updateManualTextDocument(documentId, text)
      logger.debug('✅ API call to updateManualTextDocument completed')
      
      // Refresh both documents and selections from server to ensure consistency
      const currentProject = this.appState.getCurrentProject()
      logger.debug('🔍 Current project check:', { currentProject: currentProject?.id, hasProject: !!currentProject })
      
      if (currentProject) {
        logger.debug('🔄 Refreshing project data after manual text edit...')
        await this.loadProjectDocuments(currentProject.id)
        logger.debug('✅ Documents refreshed')
        await this.loadProjectSelections(currentProject.id)
        logger.debug('✅ Selections refreshed')
        
        // Trigger UI refresh to show updated manual entry
        logger.debug('🔄 Triggering UI refresh after manual text edit...')
        window.dispatchEvent(new CustomEvent('selections-updated', { 
          detail: { source: 'manual-text-edit' } 
        }))
        
        logger.debug('✅ Project data refreshed after manual text edit')
      } else {
        logger.warn('⚠️ No current project found for refresh after manual text edit')
      }
      
      logger.debug('Manual text edit saved successfully')
      
      // Close modal if provided
      if (modal) {
        modal.close()
      }
    } catch (error) {
      logger.error('Failed to save manual text edit:', error)
      throw error
    }
  }

  /**
   * Get manual text content for editing
   */
  public async getManualTextContent(documentId: string): Promise<string> {
    try {
      logger.debug('Getting manual text content for document:', documentId)
      
      const content = await this.apiService.downloadDocument(documentId)
      return content
    } catch (error) {
      logger.error('Failed to get manual text content:', error)
      throw error
    }
  }

  /**
   * Export all text data
   */
  public exportAllText(selections: any[]): void {
    try {
      logger.debug('Exporting all text data')
      
      const textData = selections.map((selection, index) => {
        const text = selection.edited_text || selection.extracted_text || ''
        return `Selection ${index + 1}:\n${text}\n\n`
      }).join('')

      const blob = new Blob([textData], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      
      const a = document.createElement('a')
      a.href = url
      a.download = `doc2plot_selections_${new Date().toISOString().split('T')[0]}.txt`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      
      URL.revokeObjectURL(url)
      logger.debug('Text data exported successfully')
    } catch (error) {
      logger.error('Failed to export text data:', error)
      throw error
    }
  }

  /**
   * Get project statistics
   */
  public getProjectStatistics(): any {
    const selections = this.appState.getSelections()
    const coordinates = this.appState.getCoordinates()
    const documents = this.appState.getProjectDocuments()
    
    return {
      selectionsCount: selections.length,
      coordinatesCount: coordinates.length,
      documentsCount: documents.length,
      hasDescriptionList: !!this.appState.getDescriptionList(),
      lastUpdated: new Date().toISOString()
    }
  }

  /**
   * Validate project data integrity
   */
  public validateProjectData(): { isValid: boolean; errors: string[] } {
    const errors: string[] = []
    
    // Note: Coordinates are not currently stored separately in the app state
    // This validation is simplified for now
    logger.debug('Project data validation completed')
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    logger.debug('DataManager cleaned up')
  }
}
